import React from "react";
import { Activity, ShieldAlert, CheckCircle2, AlertTriangle, XCircle, TrendingUp, HelpCircle } from "lucide-react";

export default function OperationHealthCard({ operation }) {
  const healthScore = operation?.health_score ?? 50;
  const healthStatus = operation?.health_status ?? "ATTENTION";
  const helping = operation?.health_reasons_helping || [];
  const damaging = operation?.health_reasons_damaging || [];

  const getStatusColor = () => {
    if (healthScore <= 20) return "text-red-500 border-red-500/30 bg-red-500/10";
    if (healthScore <= 40) return "text-orange-500 border-orange-500/30 bg-orange-500/10";
    if (healthScore <= 60) return "text-yellow-500 border-yellow-500/30 bg-yellow-500/10";
    if (healthScore <= 80) return "text-emerald-400 border-emerald-500/30 bg-emerald-500/10";
    return "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/15";
  };

  const getStatusLabel = () => {
    if (healthScore <= 20) return "CRÍTICO";
    if (healthScore <= 40) return "FRACO";
    if (healthScore <= 60) return "ATENÇÃO";
    if (healthScore <= 80) return "SAUDÁVEL";
    return "FORTE";
  };

  const getProgressBarGradient = () => {
    if (healthScore <= 20) return "from-red-600 to-red-500";
    if (healthScore <= 40) return "from-orange-600 to-orange-500";
    if (healthScore <= 60) return "from-yellow-600 to-yellow-500";
    if (healthScore <= 80) return "from-emerald-500 to-green-400";
    return "from-[#FF6B00] via-emerald-400 to-[#10B981]";
  };

  return (
    <div
      data-testid="operation-health-card"
      className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 shadow-lg relative overflow-hidden"
    >
      {/* Background glow */}
      <div
        className={`absolute -top-16 -right-16 w-36 h-36 rounded-full blur-3xl pointer-events-none opacity-20 ${
          healthScore >= 61 ? "bg-emerald-500" : healthScore <= 40 ? "bg-red-500" : "bg-yellow-500"
        }`}
      />

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#FF6B00]" />
          <h2 className="text-sm font-bold tracking-wider text-gray-300 uppercase">
            Saúde da Operação
          </h2>
        </div>
        <div
          data-testid="health-status-badge"
          className={`text-xs font-extrabold px-2.5 py-0.5 rounded-full border ${getStatusColor()}`}
        >
          {getStatusLabel()}
        </div>
      </div>

      {/* Visual Metaphor: 🚨 [health progress bar] 💰 */}
      <div className="my-3">
        <div className="flex items-center justify-between text-xs font-semibold mb-1.5">
          <span className="flex items-center gap-1 text-red-400">
            <span>🚨</span>
            <span className="text-[11px] text-gray-400">Zona de Risco</span>
          </span>
          <span data-testid="health-score-value" className="text-xl font-extrabold text-white">
            {healthScore}%
          </span>
          <span className="flex items-center gap-1 text-emerald-400">
            <span className="text-[11px] text-gray-400">Escala Lucrativa</span>
            <span>💰</span>
          </span>
        </div>

        <div className="w-full h-3.5 bg-[#1F2430] rounded-full overflow-hidden p-0.5 border border-[#282D3D]">
          <div
            data-testid="health-progress-bar"
            className={`h-full rounded-full transition-all duration-700 bg-gradient-to-r ${getProgressBarGradient()} shadow-[0_0_12px_rgba(255,107,0,0.3)]`}
            style={{ width: `${Math.max(5, healthScore)}%` }}
          />
        </div>
      </div>

      {/* Explainability: O que está ajudando vs O que está derrubando */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 pt-3 border-t border-[#232733] text-xs">
        <div>
          <div className="flex items-center gap-1.5 font-bold text-emerald-400 mb-2">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>O que está ajudando:</span>
          </div>
          {helping.length > 0 ? (
            <ul className="space-y-1 text-gray-300">
              {helping.map((item, idx) => (
                <li key={`h-${idx}-${item}`} className="flex items-start gap-1.5">
                  <span className="text-emerald-400 font-bold">+</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-gray-400 italic">Nenhum fator positivo no momento.</div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-1.5 font-bold text-red-400 mb-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>O que está derrubando sua saúde:</span>
          </div>
          {damaging.length > 0 ? (
            <ul className="space-y-1 text-gray-300">
              {damaging.map((item, idx) => (
                <li key={`d-${idx}-${item}`} className="flex items-start gap-1.5">
                  <span className="text-red-400 font-bold">-</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-emerald-400/80 italic">Operação sem alertas prejudiciais graves.</div>
          )}
        </div>
      </div>
    </div>
  );
}
